import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import binocularsSrc from '~/assets/images/cta/binoculars.svg';
import rocketSrc from '~/assets/images/cta/rocket.svg';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';
import CtaSection from '~/ui/components/CtaSection/CtaSection';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import TextInput from '~/ui/components/TextInput/TextInput';

import AmazonS3Instruction from './AmazonS3Instruction';

function AmazonS3Auth({
  enableGuide,
  showInstruction,
  onToggleEnableGuide,
  onToggleShowInstruction,
}) {
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const [roleArn, setRoleArn] = useState('');
  const isReauthenticate = !!activeSyncPair?.isReauthenticate;
  const intl = useIntl();
  const dispatch = useDispatch();

  const [createS3TokenId, createTokenResult] =
    api.endpoints.createS3TokenId.useMutation();
  const {
    data: tokenData,
    isError: isTokenError,
    isLoading: isTokenLoading,
  } = createTokenResult;

  const [updateSyncPair, updateSyncPairResult] =
    api.endpoints.updateSyncPair.useMutation();
  const {
    data: updatedPair,
    isError: isUpdateError,
    isLoading: isUpdateLoading,
  } = updateSyncPairResult;

  useEffect(() => {
    if (isTokenError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AmazonS3Auth.failedToExchangeToken',
          defaultMessage:
            'Failed to exchange the S3 token ID. Please try again.',
          description: 'Error message when token exchange fails',
        }),
      );
    }
    if (isUpdateError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AmazonS3Auth.failedToUpdateSyncPair',
          defaultMessage: 'Failed to update Sync Pair. Please try again.',
          description: 'Error message when updating sync pair fails',
        }),
      );
    }
  }, [isTokenError, isUpdateError]);

  useEffect(() => {
    if (updatedPair) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AmazonS3Auth.syncPairUpdated',
            defaultMessage: `Sync pair has been updated: {name}`,
            description:
              'Success message when sync pair is updated. Example: name = "Documents Sync"',
          },
          {
            name: updatedPair.name,
          },
        ),
      );

      dispatch(slices.integrations.actions.addSyncPair(updatedPair));
      dispatch(
        slices.integrations.actions.setManageSyncPairStage(
          ManageSyncPairStageType.None,
        ),
      );
    }
  }, [updatedPair]);

  useEffect(() => {
    if (!isTokenLoading && tokenData) {
      if (isReauthenticate) {
        updateSyncPair({ ...activeSyncPair, tokenId: tokenData.id });
      } else {
        dispatch(
          slices.integrations.actions.setActiveSyncPair({
            ...activeSyncPair,
            tokenId: tokenData.id,
          }),
        );
        dispatch(
          slices.integrations.actions.setManageSyncPairStage(
            ManageSyncPairStageType.Folders,
          ),
        );
      }
    }
  }, [tokenData]);

  if (showInstruction && !isReauthenticate) {
    return <AmazonS3Instruction />;
  }

  if (enableGuide && !isReauthenticate) {
    return (
      <FlexLayout space={4}>
        <CtaSection
          actionButton={{
            text: intl.formatMessage({
              id: 'AmazonS3Auth.helpGuide',
              defaultMessage: 'Go to help guide',
              description: 'Button text to open help guide | maxLength=30',
            }),
            onClick: () => onToggleShowInstruction(true),
          }}
          description={intl.formatMessage({
            id: 'AmazonS3Auth.helpGuideDescription',
            defaultMessage:
              'AWS Admin: Login into your AWS Console to access bucket',
            description: 'Description for help guide button',
          })}
          imageSrc={binocularsSrc}
        />
        <CtaSection
          actionButton={{
            text: intl.formatMessage({
              id: 'AmazonS3Auth.createSyncPairs',
              defaultMessage: 'Create sync pairs',
              description: 'Button text to create sync pairs | maxLength=30',
            }),
            onClick: () => onToggleEnableGuide(false),
          }}
          description={intl.formatMessage({
            id: 'AmazonS3Auth.createSyncPairsDescription',
            defaultMessage:
              'Evisort Admin: Set up Sync pair on Evisort platform with bucket access',
            description: 'Description for create sync pairs button',
          })}
          imageSrc={rocketSrc}
        />
      </FlexLayout>
    );
  }

  return (
    <FlexLayout
      alignItems="center"
      flex="auto"
      flexDirection="column"
      space={12}
    >
      <ProviderLogo provider="AMAZON_S3" />
      <InputLabel
        id="amazon-s3-auth-input-label"
        isRequired={true}
        label={intl.formatMessage({
          id: 'AmazonS3Auth.addIamRoleArn',
          defaultMessage: 'Add IAM Role ARN',
          description: 'Label for IAM Role ARN input field',
        })}
      >
        <FlexLayout flexDirection="column" space={8}>
          <FlexLayout flexDirection="column">
            <TextInput
              placeholder="Example: arn:aws:iam::123456..."
              value={roleArn}
              width="l"
              onChange={setRoleArn}
              isTokenError={
                isTokenError
                  ? intl.formatMessage({
                      id: 'AmazonS3Auth.invalidIamRoleArn',
                      defaultMessage:
                        'Please make sure you have entered a valid IAM Role ARN.',
                      description: 'Error message for invalid IAM Role ARN',
                    })
                  : undefined
              }
            />
          </FlexLayout>
          <Button
            isLoading={isTokenLoading || isUpdateLoading}
            isFullWidth={true}
            text={
              isReauthenticate
                ? 'Reauthenticate'
                : intl.formatMessage({
                    id: 'AmazonS3Auth.checkValue',
                    defaultMessage: 'Check Value',
                    description: 'Button text to check IAM Role ARN value',
                  })
            }
            onClick={() => createS3TokenId({ roleArn })}
            variant="primary"
          />
        </FlexLayout>
      </InputLabel>
    </FlexLayout>
  );
}

export default AmazonS3Auth;
